import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import {
   aiFieldTone,
   formatAiFieldStatus,
   formatProvenanceChip,
   provenanceChipTone,
   type ANNServitutProvenance,
   type ServitutAiFieldStatus,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutAktSearchLocation, ServitutAktSearchSource } from "src/modules/ANNModule/servitutAktSearch";
import type { ServitutDocumentExtent, ServitutPageSourceStatus } from "./ServitutRedegoerelseRow";
import "./ServitutRedegoerelseRapport.css";

export function AiRobotIcon() {
   return (
      <svg
         className="servitut-redegoerelse__aiRobot"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <rect x="3" y="5.2" width="10" height="8.3" rx="2" stroke="currentColor" strokeWidth="1.35" />
         <path d="M8 5.2V2.6" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
         <circle cx="8" cy="2.15" r="1.05" stroke="currentColor" strokeWidth="1.2" />
         <circle cx="6.15" cy="8.75" r="1.05" fill="currentColor" />
         <circle cx="9.85" cy="8.75" r="1.05" fill="currentColor" />
         <path d="M6.2 11.45h3.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
   );
}

export function PersonIcon() {
   return (
      <svg
         className="servitut-redegoerelse__personIcon"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <circle cx="8" cy="5.15" r="2.35" stroke="currentColor" strokeWidth="1.35" />
         <path
            d="M3.4 13.15c.55-2.55 2.2-3.9 4.6-3.9s4.05 1.35 4.6 3.9"
            stroke="currentColor"
            strokeWidth="1.35"
            strokeLinecap="round"
         />
      </svg>
   );
}

export function ServitutMarkChip(props: {
   text: string;
   tone?: "ok" | "warn" | "bad" | "neutral";
   title?: string;
   showAi?: boolean;
   showPerson?: boolean;
}) {
   if (!props.text) {
      return null;
   }
   const markClass = props.showPerson
      ? " servitut-redegoerelse__markChip--person"
      : props.showAi
         ? " servitut-redegoerelse__markChip--ai"
         : "";
   return (
      <span
         className={`servitut-redegoerelse__markChip servitut-redegoerelse__markChip--${props.tone || "neutral"}${markClass}`}
         title={props.title || props.text}
         aria-label={props.showPerson || props.showAi ? props.text : undefined}
      >
         {props.showPerson ? <PersonIcon /> : props.showAi ? <AiRobotIcon /> : null}
         {props.text}
      </span>
   );
}

export function ServitutProvenanceChip(props: {
   provenance?: ANNServitutProvenance;
   hideAiGenerated?: boolean;
   hideFetchedFromAkt?: boolean;
}) {
   const text = formatProvenanceChip(props.provenance, {
      hideAiGenerated: props.hideAiGenerated,
      hideFetchedFromAkt: props.hideFetchedFromAkt,
   });
   if (!text) {
      return null;
   }
   return <ServitutMarkChip text={text} tone={provenanceChipTone(props.provenance)} />;
}

export function ServitutDigitalDocumentChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Digital document")}
         tone="ok"
      />
   );
}

export function ServitutPageFindChip(props: { source: ServitutPageSourceStatus }) {
   if (props.source.kind === "hidden") {
      return null;
   }
   if (props.source.kind === "user_pages") {
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("User found {count} pages", { count: props.source.count })}
            tone="ok"
            showPerson
         />
      );
   }
   if (props.source.kind === "ai_pages") {
      if (!props.source.confident) {
         return (
            <ServitutMarkChip
               text={Localization.getFormattedText("AI found {count} pages, not confident", { count: props.source.count })}
               tone="warn"
               title={props.source.reason}
               showAi
            />
         );
      }
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("AI found {count} pages", { count: props.source.count })}
            tone="ok"
            title={props.source.reason}
            showAi
         />
      );
   }
   if (props.source.kind === "ai_none") {
      return (
         <ServitutMarkChip
            text={Localization.getText("AI found no pages")}
            tone="bad"
            showAi
         />
      );
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Page search not run")}
         tone="neutral"
         showAi
      />
   );
}

export function ServitutDocumentExtentChip(props: {
   extent: ServitutDocumentExtent;
}) {
   if (props.extent.kind !== "entire_akt") {
      return null;
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Entire akt")}
         tone="neutral"
      />
   );
}

export function ServitutImportedChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Annotation imported")}
         tone="ok"
         title={Localization.getText("Using saved annotation PDF")}
      />
   );
}

export function ServitutAiFieldChip(props: { status?: ServitutAiFieldStatus; text?: string }) {
   const text = props.text || formatAiFieldStatus(props.status);
   return (
      <ServitutMarkChip
         text={text}
         tone={aiFieldTone(props.status)}
         showAi={props.status !== "person_approved" && props.status !== "not_run"}
      />
   );
}

function pointerStillOver(el: EventTarget | null): boolean {
   return el instanceof Element && el.matches(":hover");
}

function ServitutHoverWrap(props: {
   className: string;
   ariaLabel?: string;
   popoverClassName?: string;
   interactive?: boolean;
   children: ReactNode;
   popover: ReactNode;
}) {
   const wrapRef = useRef<HTMLSpanElement | null>(null);
   const popoverRef = useRef<HTMLDivElement | null>(null);
   const pinnedRef = useRef(false);
   const [open, setOpen] = useState(false);
   const [pinned, setPinned] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const closeDelayMs = props.interactive ? 280 : 80;
   pinnedRef.current = pinned;

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) {
         return;
      }
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const closePopover = () => {
      clearHideTimer();
      setPinned(false);
      setOpen(false);
   };

   const scheduleClose = () => {
      if (pinnedRef.current) {
         return;
      }
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
         if (pinnedRef.current || pointerStillOver(wrapRef.current) || pointerStillOver(popoverRef.current)) {
            return;
         }
         setOpen(false);
      }, closeDelayMs);
   };

   const togglePinned = (event: MouseEvent | KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (pinned) {
         closePopover();
         return;
      }
      setPinned(true);
      openPopover();
   };

   useLayoutEffect(() => {
      if (!open) {
         return;
      }
      updatePosition();
   }, [open, props.popover]);

   useEffect(() => {
      if (!open) {
         return;
      }
      const onReposition = () => updatePosition();
      window.addEventListener("scroll", onReposition, true);
      window.addEventListener("resize", onReposition);
      return () => {
         window.removeEventListener("scroll", onReposition, true);
         window.removeEventListener("resize", onReposition);
      };
   }, [open]);

   useEffect(() => {
      if (!open) {
         return;
      }
      const onPointerDown = (event: PointerEvent) => {
         const target = event.target;
         if (!(target instanceof Node)) {
            return;
         }
         if (wrapRef.current?.contains(target) || popoverRef.current?.contains(target)) {
            return;
         }
         closePopover();
      };
      const onKeyDown = (event: globalThis.KeyboardEvent) => {
         if (event.key === "Escape") {
            closePopover();
         }
      };
      document.addEventListener("pointerdown", onPointerDown, true);
      document.addEventListener("keydown", onKeyDown);
      return () => {
         document.removeEventListener("pointerdown", onPointerDown, true);
         document.removeEventListener("keydown", onKeyDown);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   return (
      <>
         <span
            ref={wrapRef}
            className={props.className}
            aria-label={props.ariaLabel}
            aria-expanded={open}
            aria-haspopup={props.interactive ? "dialog" : undefined}
            data-pinned={pinned ? "true" : undefined}
            role={props.interactive ? "button" : undefined}
            tabIndex={props.interactive ? 0 : undefined}
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
            onClick={props.interactive ? togglePinned : undefined}
            onKeyDown={props.interactive ? (event) => {
               if (event.key === "Enter" || event.key === " ") {
                  togglePinned(event);
               }
            } : undefined}
         >
            {props.children}
         </span>
         {open ? createPortal(
            <div
               ref={popoverRef}
               className={props.popoverClassName || "servitut-redegoerelse__sourceHintPopover"}
               style={{ top: pos.top, left: pos.left }}
               role={props.interactive ? "dialog" : "tooltip"}
               onMouseEnter={clearHideTimer}
               onMouseLeave={scheduleClose}
               onPointerDown={clearHideTimer}
            >
               {props.popover}
            </div>,
            document.body,
         ) : null}
      </>
   );
}

export function ServitutSourceHint(props: {
   mark: string;
   text: string;
   tone: "annotation" | "rigsarkiv" | "missing";
}) {
   return (
      <ServitutHoverWrap
         className={`servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--${props.tone}`}
         ariaLabel={props.text}
         popover={props.text}
      >
         {props.mark}
      </ServitutHoverWrap>
   );
}

export function aktSearchLocationLabel(source: ServitutAktSearchSource): string {
   if (source === "korrektAkt") {
      return Localization.getText("Correct akt in text");
   }
   if (source === "servitutAkt") {
      return Localization.getText("Direct akt link from Tingbogen");
   }
   if (source === "textAkt") {
      return Localization.getText("Akt number in Tingbogen");
   }
   return Localization.getText("Other akt (øvrige akt)");
}

function normalizeAktKey(value?: string | null): string {
   return (value || "").trim().toLowerCase().replace(/\s+/g, "");
}

export function ServitutAktSourcePill(props: {
   aktId?: string | null;
   usedSource?: ServitutAktSearchSource | "customAkt";
   locations: ServitutAktSearchLocation[];
   fallbackLabel?: string;
   fileLabel?: string;
   disabled?: boolean;
   busy?: boolean;
   onSelectLocation?: (location: ServitutAktSearchLocation) => void;
   onFetchAkt?: (aktId: string) => void;
}) {
   const fileUsed = Boolean(props.fileLabel?.trim());
   const customUsed = !fileUsed && props.usedSource === "customAkt";
   const fetchInputId = useId();
   const [customAktId, setCustomAktId] = useState(
      customUsed ? (props.aktId || "") : "",
   );
   const displayAkt = fileUsed
      ? props.fileLabel
      : (
         props.aktId?.trim()
         || props.locations.find((location) => location.source === props.usedSource)?.aktId
         || props.locations.find((location) => location.aktId)?.aktId
         || props.fallbackLabel
         || ""
      );
   const label = displayAkt || Localization.getText("Not found");
   const usedAktKey = fileUsed ? "" : normalizeAktKey(props.aktId || displayAkt);

   function submitCustomAkt() {
      const value = customAktId.trim();
      if (!value || props.disabled || props.busy) {
         return;
      }
      props.onFetchAkt?.(value);
   }

   return (
      <ServitutHoverWrap
         className="servitut-redegoerelse__aktSourcePill"
         ariaLabel={label}
         interactive
         popoverClassName="servitut-redegoerelse__sourceHintPopover servitut-redegoerelse__aktSourcePopover"
         popover={(
            <>
               <p className="servitut-redegoerelse__aktSourceIntro">
                  {Localization.getText("Here you find the priority of akt id which we believe the servitut might be in.")}
               </p>
               {fileUsed ? (
                  <p className="servitut-redegoerelse__aktSourceIntro">
                     {Localization.getFormattedText("Using uploaded PDF {filename}. Choose an akt below to switch back.", {
                        filename: props.fileLabel,
                     })}
                  </p>
               ) : null}
               <ol className="servitut-redegoerelse__aktSourceList">
                  {props.locations.map((location, index) => {
                     const isUsed = !fileUsed && !customUsed && (
                        (usedAktKey && normalizeAktKey(location.aktId) === usedAktKey)
                        || (!usedAktKey && location.source === props.usedSource)
                     );
                     const selectable = Boolean(props.onSelectLocation && location.aktId && !props.disabled);
                     const className = `servitut-redegoerelse__aktSourceRow${isUsed ? " servitut-redegoerelse__aktSourceRow--used" : ""}${selectable ? " servitut-redegoerelse__aktSourceRow--action" : ""}`;
                     const body = (
                        <>
                           <span className="servitut-redegoerelse__aktSourceRowIndex" aria-hidden="true">
                              {index + 1}.
                           </span>
                           <span className="servitut-redegoerelse__aktSourceRowLabel">
                              {aktSearchLocationLabel(location.source)}
                           </span>
                           <span className="servitut-redegoerelse__aktSourceRowValue">
                              {location.aktId || "—"}
                              {isUsed ? ` · ${Localization.getText("Used")}` : ""}
                           </span>
                        </>
                     );
                     return selectable ? (
                        <li key={location.source}>
                           <button
                              type="button"
                              className={className}
                              disabled={props.busy}
                              onClick={(event) => {
                                 event.preventDefault();
                                 event.stopPropagation();
                                 props.onSelectLocation?.(location);
                              }}
                           >
                              {body}
                           </button>
                        </li>
                     ) : (
                        <li key={location.source} className={className}>
                           {body}
                        </li>
                     );
                  })}
               </ol>
               {props.onFetchAkt ? (
                  <>
                     <div className="servitut-redegoerelse__aktSourceDivider" role="separator" />
                     <form
                        className={`servitut-redegoerelse__aktSourceRow servitut-redegoerelse__aktSourceFetch${customUsed ? " servitut-redegoerelse__aktSourceRow--used" : ""}`}
                        onSubmit={(event) => {
                           event.preventDefault();
                           event.stopPropagation();
                           submitCustomAkt();
                        }}
                     >
                        <span className="servitut-redegoerelse__aktSourceRowIndex" aria-hidden="true">
                           5.
                        </span>
                        <label className="servitut-redegoerelse__aktSourceRowLabel" htmlFor={fetchInputId}>
                           {Localization.getText("Fetch akt yourself")}
                        </label>
                        <span className="servitut-redegoerelse__aktSourceFetchValue">
                           <input
                              id={fetchInputId}
                              className="servitut-redegoerelse__aktSourceInput"
                              type="text"
                              value={customAktId}
                              placeholder="51_M_205"
                              disabled={props.disabled || props.busy}
                              onChange={(event) => setCustomAktId(event.target.value)}
                              onClick={(event) => event.stopPropagation()}
                              onPointerDown={(event) => event.stopPropagation()}
                           />
                           <button
                              type="submit"
                              className="servitut-redegoerelse__aktSourceUploadButton"
                              disabled={props.disabled || props.busy || !customAktId.trim()}
                           >
                              {props.busy ? Localization.getText("Loading...") : Localization.getText("Use akt")}
                           </button>
                        </span>
                     </form>
                  </>
               ) : null}
            </>
         )}
      >
         {label}
      </ServitutHoverWrap>
   );
}
